import type { PollView } from '@leanin/shared';
import { POLL_VOTER_PREVIEW_LIMIT } from '@leanin/shared/polls';

/**
 * The optimistic cache shape for casting or changing a poll vote, so the
 * check, the fills and the voter faces never disagree while the request is in
 * flight.
 *
 * - Hidden poll (`results === null`): only the viewer's choice moves. Real
 *   tallies arrive with the response; hidden counts are never fabricated.
 * - Revealed poll (a vote change, or the author's first vote): one vote moves
 *   onto the new option (off the old one, if any) and, on a public poll, the
 *   viewer's own preview entry moves with it. When the viewer has no entry to
 *   move (not previewed yet), no face is invented — the response fills it in.
 */
export function applyOptimisticPollVote(
  poll: PollView,
  optionId: string,
  viewerId: string | null,
): PollView {
  const previous = poll.viewerVotedOptionId;
  if (previous === optionId) return poll;
  if (poll.results === null) return { ...poll, viewerVotedOptionId: optionId };

  const recount = (tallyOptionId: string, voteCount: number): number =>
    tallyOptionId === optionId
      ? voteCount + 1
      : tallyOptionId === previous
        ? voteCount - 1
        : voteCount;
  const totalVotes = previous === null ? poll.results.totalVotes + 1 : poll.results.totalVotes;

  if (poll.visibility === 'anonymous') {
    return {
      ...poll,
      viewerVotedOptionId: optionId,
      results: {
        totalVotes,
        tallies: poll.results.tallies.map((t) => ({
          ...t,
          voteCount: recount(t.optionId, t.voteCount),
        })),
      },
    };
  }

  const mine =
    viewerId === null
      ? undefined
      : poll.results.tallies
          .find((t) => t.optionId === previous)
          ?.voters.find((v) => v.userId === viewerId);
  return {
    ...poll,
    viewerVotedOptionId: optionId,
    results: {
      totalVotes,
      tallies: poll.results.tallies.map((t) => {
        const others = t.voters.filter((v) => v.userId !== viewerId);
        return {
          ...t,
          voteCount: recount(t.optionId, t.voteCount),
          voters:
            t.optionId === optionId && mine !== undefined
              ? [mine, ...others].slice(0, POLL_VOTER_PREVIEW_LIMIT)
              : t.optionId === previous
                ? others
                : t.voters,
        };
      }),
    },
  };
}
